import React, {useContext, useState, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {
  MISSING_ID,
  currentYyMm,
  getCalendarMatrix,
  buildSlotsByDate,
  yymmToYearMonth,
} from '@common/utils'
import {
  formatDateLegend,
  formatDateTimeLocal,
  parseDateTimeLocal,
  parseSlotDateTime,
} from '@common/utils/dates'

import {AppointmentDetailsCard} from './RescheduleAppointmentModalV2.styled'

import {
  Grid,
  Column,
  SelectedGlobal,
} from '~/components/bookings/BookingsPortalPage.styled'
import {CalendarPanel} from '~/components/bookings/CalendarPanel'
import {DayTimePanel} from '~/components/bookings/DayTimePanel'
import {SideModal} from '~/components/general/modal/SideModal'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Input} from '~/components/general/input/Input'
import {Checkbox} from '~/components/general/checkbox/Checkbox'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {
  useRescheduleAppointmentForFacility,
  useGetPractitionerAvailabilities,
} from '~/api/practitioner'
import {useMonthNavigation} from '~/hooks/useMonthNavigation'
import {useWeekdayShorts} from '~/hooks/useWeekdayShorts'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'

interface RescheduleAppointmentModalV2Props {
  isVisible: boolean
  onClose: () => void
  appointmentId: string
  facilityId: string
  originalDate: string
  patientName: string
  patientDob: string | null
  department: string | null
}

type AvailabilityDay = {date: string; slots: string[]}

export const RescheduleAppointmentModalV2: React.FC<
  RescheduleAppointmentModalV2Props
> = ({
  isVisible,
  onClose,
  appointmentId,
  facilityId,
  originalDate,
  patientName,
  patientDob,
  department,
}) => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const [apiMonthSpec, setApiMonthSpec] = useState<string>('FIRST')
  const [calendarYyMm, setCalendarYyMm] = useState<string>(currentYyMm())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [createCustomSlot, setCreateCustomSlot] = useState<boolean>(false)
  const [customDateTime, setCustomDateTime] = useState<string>(() => {
    const timeNow = new Date()
    const lastTenMinRounded = new Date(
      timeNow.setMinutes(Math.floor(timeNow.getMinutes() / 10) * 10),
    )
    return formatDateTimeLocal(lastTenMinRounded)
  })

  const {mutate, isPending: isUpdating} = useRescheduleAppointmentForFacility({
    facilityId,
    appointmentId,
  })

  const effectiveLocation = department ?? ''

  const {
    data: avail,
    isLoading: isLoadingAvailabilities,
    isFetching: isFetchingAvailabilities,
  } = useGetPractitionerAvailabilities(
    {
      facilityId: facilityId || MISSING_ID,
      serviceName: effectiveLocation,
      monthSpec: apiMonthSpec,
    },
    {
      enabled: isVisible && !!facilityId && !createCustomSlot,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    },
  )

  const days = useMemo<AvailabilityDay[]>(
    () => (avail?.days as AvailabilityDay[]) ?? [],
    [avail?.days],
  )

  const didSetCalendarFromApiRef = useRef(false)

  useEffect(() => {
    if (!isVisible) {
      setApiMonthSpec('FIRST')
      setCalendarYyMm(currentYyMm())
      setSelectedDate(null)
      setSelectedTime(null)
      setCreateCustomSlot(false)
      didSetCalendarFromApiRef.current = false
    }
  }, [isVisible])

  useEffect(() => {
    if (!avail || !isVisible) return
    if (didSetCalendarFromApiRef.current) return
    const yymm = avail?.yearMonth
    if (yymm) setCalendarYyMm(yymm)
    didSetCalendarFromApiRef.current = true
  }, [avail, isVisible])

  const cells = useMemo(() => getCalendarMatrix(calendarYyMm), [calendarYyMm])

  const selectedDateLegend = useMemo(
    () =>
      formatDateLegend(selectedDate || undefined, selectedTime || undefined),
    [selectedDate, selectedTime],
  )

  const slotsByDate = useMemo(() => buildSlotsByDate(days), [days])

  useEffect(() => {
    if (!isVisible || !effectiveLocation || createCustomSlot) return
    const first = days.find((d: AvailabilityDay) => (d.slots?.length || 0) > 0)
    if (first) {
      setSelectedDate(first.date)
    } else if (avail) {
      const {year, month} = yymmToYearMonth(calendarYyMm)
      const firstDayOfMonth = `${year}/${String(month).padStart(2, '0')}/01`
      setSelectedDate(firstDayOfMonth)
    }
    setSelectedTime(null)
  }, [
    isVisible,
    effectiveLocation,
    days,
    avail,
    calendarYyMm,
    createCustomSlot,
  ])

  const weekdayShorts = useWeekdayShorts()

  const {gotoPrevMonth, gotoNextMonth, monthLabel, disablePrev} =
    useMonthNavigation({
      currentYyMm: calendarYyMm,
      onMonthChange: (yyMm) => {
        setCalendarYyMm(yyMm)
        setApiMonthSpec(yyMm)
      },
    })

  const handleRescheduleAppointment = () => {
    let appointmentDate: Date

    if (createCustomSlot) {
      appointmentDate = parseDateTimeLocal(customDateTime)
    } else if (selectedDate && selectedTime) {
      appointmentDate = parseSlotDateTime(selectedDate, selectedTime)
    } else {
      return
    }

    mutate(
      {
        newAppointmentDate: appointmentDate.toISOString(),
      },
      {
        onSuccess: () => {
          addNotification(
            `${t('Appointment successfully rescheduled')}\n${t(
              'From: {{from}}\nTo: {{to}}',
              {
                from: new Date(originalDate).toLocaleString(),
                to: appointmentDate.toLocaleString(),
              },
            )}`,
            NotificationType.Success,
          )
          onClose()
        },
        onError: (error) => {
          addNotification(
            `${t('Error rescheduling appointment')}\n${
              error instanceof Error
                ? error.message
                : t('Unknown error occurred')
            }`,
            NotificationType.Error,
          )
        },
      },
    )
  }

  const canReschedule =
    !isUpdating &&
    (createCustomSlot ? !!customDateTime : !!selectedDate && !!selectedTime)

  const showProcessingOverlay =
    isUpdating ||
    ((isFetchingAvailabilities || isLoadingAvailabilities) &&
      !!effectiveLocation &&
      !createCustomSlot)

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      modalSize="wide"
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {t('Reschedule Appointment')}
          </Typography>
        </Container>
      }>
      <AppointmentDetailsCard>
        <Container flex direction="column" spacing={2}>
          <Typography size="md" weight="medium">
            {t('Patient Information')}
          </Typography>
          <Container flex direction="column" spacing={1}>
            <Typography weight="medium" size="md">
              {t('Name')}
            </Typography>
            <Typography color="secondary" size="sm">
              {patientName}
            </Typography>

            {patientDob && (
              <>
                <Typography weight="medium" size="md">
                  {t('Date of Birth')}
                </Typography>
                <Typography color="secondary" size="sm">
                  {new Date(patientDob).toLocaleDateString()}
                </Typography>
              </>
            )}
          </Container>

          <Typography size="md" weight="medium">
            {t('Appointment Details')}
          </Typography>

          <Container flex direction="column" spacing={1}>
            <Typography weight="medium" size="md">
              {t('Current appointment date')}
            </Typography>
            <Typography color="secondary" size="sm">
              {new Date(originalDate).toLocaleString()}
            </Typography>
          </Container>
        </Container>
      </AppointmentDetailsCard>

      <AppointmentDetailsCard>
        <ProcessingOverlay isVisible={showProcessingOverlay} />
        <Container flex direction="column" spacing={2}>
          <Typography size="md" weight="medium">
            {t('New Appointment Date')}
          </Typography>

          {!createCustomSlot && (
            <>
              {selectedDate && (
                <SelectedGlobal>
                  <Typography size="xl" weight="bold">
                    {selectedDateLegend}
                  </Typography>
                </SelectedGlobal>
              )}
              <Grid>
                <Column>
                  <CalendarPanel
                    dateTitle={t('DATE')}
                    monthLabel={monthLabel}
                    weekdayShorts={weekdayShorts}
                    cells={cells}
                    slotsByDate={slotsByDate}
                    selectedDate={selectedDate}
                    onSelectDate={(dateKey) => {
                      setSelectedDate(dateKey)
                      setSelectedTime(null)
                    }}
                    disablePrev={disablePrev}
                    onPrevMonth={gotoPrevMonth}
                    onNextMonth={gotoNextMonth}
                  />
                </Column>
                <Column>
                  <DayTimePanel
                    title={t('TIME')}
                    selectedDate={selectedDate}
                    selectedDateSlots={
                      selectedDate ? slotsByDate.get(selectedDate) || [] : []
                    }
                    selectedTime={selectedTime}
                    onSelectTime={(time) => setSelectedTime(time)}
                    isLoading={isUpdating}
                    timeZoneNotice={undefined}
                    noAvailabilityText={t(
                      'There is no availability on this date. Please choose another one.',
                    )}
                  />
                </Column>
              </Grid>
            </>
          )}

          <Container flex direction="column" spacing={1}>
            <Checkbox
              checked={createCustomSlot}
              onChange={() => {
                const newValue = !createCustomSlot
                setCreateCustomSlot(newValue)
                if (newValue) {
                  setSelectedDate(null)
                  setSelectedTime(null)
                }
              }}
              label={t('Define a new slot')}
            />
          </Container>

          {createCustomSlot && (
            <Container flex direction="column" spacing={1}>
              <Typography>{t('Date and time')}</Typography>
              <Input
                type="datetime-local"
                value={customDateTime}
                onChange={(e) => {
                  setCustomDateTime(e.target.value)
                }}
              />
            </Container>
          )}

          <Container flex justify="flex-end">
            <Button
              onClick={handleRescheduleAppointment}
              disabled={!canReschedule}>
              {t('Confirm')}
            </Button>
          </Container>
        </Container>
      </AppointmentDetailsCard>
    </SideModal>
  )
}
